import { Box, Typography } from '@mui/material';
import { concat, get } from 'lodash';
import React, { FC } from 'react';
import CreateTable from 'src/components/ui-component/form-component/create-form/CreateTable';
import MarkdownRenderer from 'src/components/ui-component/markdown-renderer/MarkdownRenderer';
import { useApplication } from 'src/context/ApplicationContext';
import { FOERDERPROGRAMM_ERKLAERUNG_UND_UNTERLAGEN_PATHS } from 'src/lib/constants/componentsPath';
import {
	ALS_ERLEDIGT_MARKIEREN_LABEL,
	ANTRAG_TYP,
	ART_DES_ANTRAGS,
	FORM_FIELDS_TYPES,
	PLATZHALTER_TEXT,
	PRUEF_ASSISTENZ_STATUS,
} from 'src/lib/constants/constant';
import { FormStrucItem } from 'src/lib/types/types';
import { ForceTrue } from 'src/lib/validation/ValidationTypes';
import PruefAssistenz from 'src/pages/private/online-antrag/antrag-components/PruefAssistenz';
import { checkIfPruefAssitenzNeedsToValidate } from 'src/pages/private/online-antrag/application-helper-function/AppHelperfunctions';
import { addIndexesToPath, checkReadOnlyInUeberarbeitung } from 'src/utils/helper';
import ApplicationHeading from '../../../../application-heading/ApplicationHeading';

// import schemas
import AntragSchema from 'src/lib/paths/antrag_FULL_Paths.json';
const P = AntragSchema.attributes;

const ErklaerungGlobal: FC<{ hasComment?: boolean }> = ({ hasComment }) => {
	const { application, setApplicationProperty, readOnly } = useApplication();
	const updatedReadOnly = checkReadOnlyInUeberarbeitung(application, readOnly, hasComment);

	const antragsTyp = get(application, P.Allgemein_Typ.path, '');
	const Foerderprogramm = get(application, P.Foerderprogramm.path, {});
	const isFormantrag = get(application, P.Allgemein_Art_Des_Antrags.path, '') === ART_DES_ANTRAGS.Formantrag;

	// Dynamische Pfade basierend auf antragsTyp
	const paths =
		antragsTyp === ANTRAG_TYP.Skizze ? FOERDERPROGRAMM_ERKLAERUNG_UND_UNTERLAGEN_PATHS.Skizze
		: isFormantrag ? FOERDERPROGRAMM_ERKLAERUNG_UND_UNTERLAGEN_PATHS.Formantrag
		: FOERDERPROGRAMM_ERKLAERUNG_UND_UNTERLAGEN_PATHS.Antrag;

	return (
		<>
			<ApplicationHeading title='Erklärung' description={PLATZHALTER_TEXT} />

			<PruefAssistenz
				statusPath={P.Erklaerungen_Pruefung_Pruefstatus.path}
				hinweisPath={P.Erklaerungen_Pruefung_Hinweis_Fuer_Den_Antragsteller.path}
				ueberarbeitetAmPath={P.Erklaerungen_Pruefung_Ueberarbeitet_Am.path}
				erledigtPath={P.Erklaerungen_Pruefung_Erledigt.path}
				formStruc={formStrucPruefAssistenz(application)}
			/>

			{get(application, P.Erklaerungen_Pflichterklaerungen.path, []).length > 0 && (
				<Box>
					<Typography variant='h5' sx={{ mb: '1rem' }}>
						{' '}
						{get(Foerderprogramm, paths.PflichtErklaerungTitel, 'Pflichterklärungen')}{' '}
					</Typography>
					<MarkdownRenderer markdown={get(Foerderprogramm, paths.PflichtErklaerungBeschreibung, '')} />

					<CreateTable
						formStruc={formStrucPflichtErklaerung(application)}
						formValues={application}
						updateFormValuesFunc={setApplicationProperty}
						readOnly={updatedReadOnly}
						size='small'
					/>
				</Box>
			)}

			{/**
			 * Platzhalter, um Abstand zwischen Pflicht- und optionalen Erklärungen zu schaffen, falls beide vorhanden sind.
			 */}
			{(
				get(application, P.Erklaerungen_Pflichterklaerungen.path, []).length > 0 &&
				get(application, P.Erklaerungen_Optionale_Erklaerungen.path, []).length > 0
			) ?
				<Box sx={{ mt: '2rem' }}></Box>
			:	null}

			{get(application, P.Erklaerungen_Optionale_Erklaerungen.path, []).length > 0 && (
				<Box>
					<Typography variant='h5' sx={{ mb: '1rem' }}>
						{' '}
						{get(Foerderprogramm, paths.OptionaleErklaerungTitel, 'Optionale Erklärungen')}{' '}
					</Typography>
					<MarkdownRenderer markdown={get(Foerderprogramm, paths.OptionaleErklaerungBeschreibung, '')} />
					<CreateTable
						formStruc={formStrucOptionaleErklaerungen(application)}
						formValues={application}
						updateFormValuesFunc={setApplicationProperty}
						readOnly={updatedReadOnly}
						size='small'
					/>
				</Box>
			)}
		</>
	);
};

export default ErklaerungGlobal;

const formStrucPruefAssistenz = (application: any) => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Erklaerungen_Pruefung_Erledigt,
					label: ALS_ERLEDIGT_MARKIEREN_LABEL,
					validation:
						get(application, P.Erklaerungen_Pruefung_Pruefstatus.path) === PRUEF_ASSISTENZ_STATUS.uberarbeitung ?
							[{ validateFunction: ForceTrue }]
						:	[],
				},
			],
		},
	];
	return struc;
};
const formStrucPflichtErklaerung = (application: any) => {
	let struc: FormStrucItem[] = [];

	const pflichtErklaerungen = get(application, P.Erklaerungen_Pflichterklaerungen.path, []);
	for (const [i, ele] of pflichtErklaerungen.entries()) {
		struc.push({
			fields: [
				{
					type: FORM_FIELDS_TYPES.boolean,
					path: addIndexesToPath(
						P.Erklaerungen_Pflichterklaerungen.schema.attributes.Zugestimmt.path,
						[`${i}`],
						['Erklaerungen_Pflichterklaerungen'],
					),
					validation: [{ validateFunction: ForceTrue }],
				},
				{
					type: FORM_FIELDS_TYPES.label,
					path: 't.t',
					label: get(ele, 'Inhalt', ''),
				},
			],
		});
	}
	return struc;
};
const formStrucOptionaleErklaerungen = (application: any) => {
	let struc: FormStrucItem[] = [];
	const optionaleErklaerungen = get(application, P.Erklaerungen_Optionale_Erklaerungen.path, []);

	for (const [i, ele] of optionaleErklaerungen.entries()) {
		struc.push({
			fields: [
				{
					type: 'boolean',
					path: addIndexesToPath(
						P.Erklaerungen_Optionale_Erklaerungen.schema.attributes.Zugestimmt.path,
						[`${i}`],
						['Erklaerungen_Optionale_Erklaerungen'],
					),
				},
				{
					type: 'label',
					path: 't.t',
					label: get(ele, 'Inhalt', ''),
				},
			],
		});
	}
	return struc;
};

export const getFormStrucErklaerungGlobal = (application: any) => {
	const isHinweisFieldRequired = checkIfPruefAssitenzNeedsToValidate(
		application,
		P.Erklaerungen_Pruefung_Pruefstatus.path,
		P.Erklaerungen_Pruefung_Erledigt.path,
	);

	if (isHinweisFieldRequired) return concat(formStrucPruefAssistenz(application), formStrucPflichtErklaerung(application));

	return concat(formStrucPflichtErklaerung(application));
};
